import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiLock, FiMail, FiLogIn, FiAlertCircle, FiLogOut, FiUser, FiShield } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { auth, analytics, logEvent } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const MemberLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  // Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        // Google Analytics - login event
        if (analytics) {
          logEvent(analytics, 'login', {
            method: 'email'
          });
        }
        // Redirect naar dashboard of ledengedeelte
        navigate('/leden-dashboard');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Google Analytics page view
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: 'Leden Login',
        page_location: window.location.href
      });
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Zet persistentie op local (onthoud gebruiker)
      await setPersistence(auth, browserLocalPersistence);
      
      // Probeer in te loggen
      await signInWithEmailAndPassword(auth, email, password);
      
      setSuccess('Succesvol ingelogd! U wordt doorgestuurd...');
      
      // Google Analytics - login success
      if (analytics) {
        logEvent(analytics, 'login_success', {
          method: 'email'
        });
      }
      
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Vertaal Firebase error codes naar Nederlandse berichten
      let errorMessage = 'Er is een fout opgetreden bij het inloggen.';
      
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'Geen account gevonden met dit e-mailadres.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Onjuist wachtwoord.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Ongeldig e-mailadres.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Dit account is uitgeschakeld.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Te veel mislukte pogingen. Probeer het later opnieuw.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Netwerkfout. Controleer uw internetverbinding.';
          break;
        default:
          errorMessage = err.message || 'Er is een fout opgetreden bij het inloggen.';
      }
      
      setError(errorMessage);
      
      // Google Analytics - login error
      if (analytics) {
        logEvent(analytics, 'login_error', {
          method: 'email',
          error_code: err.code
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setSuccess('U bent succesvol uitgelogd.');
      
      // Google Analytics - logout
      if (analytics) {
        logEvent(analytics, 'logout');
      }
      
      // Redirect naar home
      navigate('/');
    } catch (err: any) {
      console.error('Logout error:', err);
      setError('Er is een fout opgetreden bij het uitloggen.');
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setError('');
    setSuccess('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setSuccess('Een e-mail met instructies voor het resetten van uw wachtwoord is verzonden.');
      setShowResetForm(false);
      setResetEmail('');
      
      // Google Analytics - password reset
      if (analytics) {
        logEvent(analytics, 'password_reset_requested');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      
      let errorMessage = 'Er is een fout opgetreden bij het verzenden van de reset e-mail.';
      
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'Geen account gevonden met dit e-mailadres.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Ongeldig e-mailadres.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Te veel verzoeken. Probeer het later opnieuw.';
          break;
        default:
          errorMessage = err.message || 'Er is een fout opgetreden bij het verzenden van de reset e-mail.';
      }
      
      setError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  // Als gebruiker al is ingelogd, toon dashboard
  if (user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.div variants={fadeIn} className="mb-8">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <FiUser className="text-3xl text-indigo-900" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Welkom, {user.email}
              </h1>
              <p className="text-gray-300">
                U bent succesvol ingelogd in het ledengedeelte.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-4">
              <button
                onClick={() => navigate('/leden-dashboard')}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FiShield className="inline mr-2" />
                Ga naar Ledengedeelte
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FiLogOut className="inline mr-2" />
                Uitloggen
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black opacity-20"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Leden <span className="text-teal-400">Login</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Welkom terug! Log in om toegang te krijgen tot het ledengedeelte van Lions Club Hoorn De Doelen.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Login Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-200 dark:border-gray-700"
            >
              <motion.h2 
                variants={fadeIn}
                className="text-2xl font-bold mb-6 text-indigo-900 dark:text-teal-400 text-center"
              >
                Inloggen
              </motion.h2>
              
              {error && (
                <motion.div 
                  variants={fadeIn}
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center"
                >
                  <FiAlertCircle className="mr-2 flex-shrink-0" />
                  <p>{error}</p>
                </motion.div>
              )}
              
              {success && (
                <motion.div 
                  variants={fadeIn}
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
                >
                  <FiAlertCircle className="mr-2 flex-shrink-0" />
                  <p>{success}</p>
                </motion.div>
              )}

              {!showResetForm ? (
                <motion.form 
                  variants={fadeIn}
                  onSubmit={handleLogin}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">E-mailadres</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-teal-400 focus:border-transparent transition-colors"
                        placeholder="uw@email.nl"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">Wachtwoord</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiLock className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-teal-400 focus:border-transparent transition-colors"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Onthoud mij
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowResetForm(true)}
                      className="text-sm text-indigo-600 dark:text-teal-400 hover:underline"
                    >
                      Wachtwoord vergeten?
                    </button>
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Inloggen...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <FiLogIn className="mr-2" /> Inloggen
                        </span>
                      )}
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.form 
                  variants={fadeIn}
                  onSubmit={handlePasswordReset}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="reset-email" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">E-mailadres</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="reset-email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-teal-400 focus:border-transparent transition-colors"
                        placeholder="uw@email.nl"
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {resetLoading ? (
                        <span className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Versturen...
                        </span>
                      ) : (
                        'Reset E-mail Versturen'
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setShowResetForm(false)}
                      className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Annuleren
                    </button>
                  </div>
                </motion.form>
              )}
            </motion.div>

            <motion.div
              variants={fadeIn}
              className="mt-8 text-center"
            >
              <p className="text-gray-600 dark:text-gray-300">
                Nog geen lid? <a href="/lid-worden" className="text-indigo-600 dark:text-teal-400 hover:underline font-medium">Word lid van Lions Club Hoorn De Doelen</a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-6 text-indigo-900 dark:text-teal-400"
            >
              Ledengedeelte
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg mb-6 text-gray-700 dark:text-gray-300"
            >
              In het ledengedeelte vindt u interne informatie over:
            </motion.p>
            <motion.ul 
              variants={fadeIn}
              className="text-left max-w-md mx-auto space-y-3 text-gray-700 dark:text-gray-300 mb-8"
            >
              <li className="flex items-center">
                <span className="bg-indigo-600 dark:bg-teal-400 rounded-full w-2 h-2 mr-3 flex-shrink-0"></span>
                Vergaderingen en notulen
              </li>
              <li className="flex items-center">
                <span className="bg-indigo-600 dark:bg-teal-400 rounded-full w-2 h-2 mr-3 flex-shrink-0"></span>
                Ledenlijst en contactgegevens
              </li>
              <li className="flex items-center">
                <span className="bg-indigo-600 dark:bg-teal-400 rounded-full w-2 h-2 mr-3 flex-shrink-0"></span>
                Interne documenten en formulieren
              </li>
              <li className="flex items-center">
                <span className="bg-indigo-600 dark:bg-teal-400 rounded-full w-2 h-2 mr-3 flex-shrink-0"></span>
                Projectplanning en -evaluatie
              </li>
              <li className="flex items-center">
                <span className="bg-indigo-600 dark:bg-teal-400 rounded-full w-2 h-2 mr-3 flex-shrink-0"></span>
                Financiële rapportages
              </li>
            </motion.ul>
            <motion.p 
              variants={fadeIn}
              className="text-gray-700 dark:text-gray-300"
            >
              Heeft u problemen met inloggen? Neem contact op met de webmaster via{' '}
              <a href="mailto:webmaster@lionshoorn.nl" className="text-indigo-600 dark:text-teal-400 hover:underline font-medium">
                webmaster@lionshoorn.nl
              </a>.
            </motion.p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MemberLogin; 